import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Clock, AlertTriangle, CheckCircle, TrendingDown, AlertCircle } from 'lucide-react';
import { LoadingState } from '../ui';
import useAnalyticsStore from '../../stores/analyticsStore';
import { getSLAOverview, getSLABreachAnalysis } from '../../services/analyticsService';

const SLAComplianceTab = () => {
  const { dateRange } = useAnalyticsStore();

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['sla-overview', dateRange],
    queryFn: () => getSLAOverview({ days: dateRange }),
    staleTime: 60000
  });

  const { data: breaches, isLoading: breachesLoading } = useQuery({
    queryKey: ['sla-breaches', dateRange],
    queryFn: () => getSLABreachAnalysis({ days: dateRange }),
    staleTime: 60000
  });

  if (overviewLoading || breachesLoading) {
    return <LoadingState message="Loading SLA compliance data..." />;
  }

  if (!overview) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No SLA data available for the selected period</p>
      </div>
    );
  }

  const summary = overview.summary || {};
  const slaByState = overview.sla_by_state || [];
  const dailyTrend = overview.daily_trend || [];

  // Prepare daily trend data
  const trendData = dailyTrend.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    compliant: d.compliant,
    breached: d.breached,
    compliance_rate: ((d.compliant / d.total) * 100).toFixed(1)
  }));

  // Colors
  const COLORS = {
    compliant: '#10B981',
    breached: '#EF4444',
    atRisk: '#F59E0B'
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="SLA Compliance Rate"
          value={`${summary.compliance_rate || 0}%`}
          icon={CheckCircle}
          color="green"
          trend={summary.compliance_rate >= 90 ? 'up' : 'down'}
        />
        <KPICard
          title="SLA Breached"
          value={summary.sla_breached || 0}
          icon={AlertTriangle}
          color="red"
        />
        <KPICard
          title="At Risk"
          value={summary.at_risk || 0}
          icon={TrendingDown}
          color="orange"
        />
        <KPICard
          title="Total Workflows"
          value={summary.total_workflows || 0}
          icon={Clock}
          color="blue"
        />
      </div>

      {/* Daily Compliance Trend */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily SLA Compliance Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="compliant"
              stroke={COLORS.compliant}
              strokeWidth={2}
              name="Compliant"
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="breached"
              stroke={COLORS.breached}
              strokeWidth={2}
              name="Breached"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="compliance_rate"
              stroke="#3B82F6"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Compliance %"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* SLA by Workflow State */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">SLA Compliance by Workflow State</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={slaByState} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 100]} />
            <YAxis type="category" dataKey="state" width={120} fontSize={11} />
            <Tooltip />
            <Bar dataKey="compliance_rate" fill="#3B82F6" radius={[0, 8, 8, 0]}>
              {slaByState.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.compliance_rate >= 90 ? COLORS.compliant : entry.compliance_rate >= 70 ? COLORS.atRisk : COLORS.breached}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent SLA Breaches Table */}
      {breaches && breaches.breach_details && breaches.breach_details.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Recent SLA Breaches ({breaches.total_breaches})
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SLA Target
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actual Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Breach Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {breaches.breach_details.slice(0, 20).map((breach, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {breach.workflow_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {breach.supplier || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {breach.current_state}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {breach.sla_target_minutes} min
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {breach.actual_duration_minutes.toFixed(0)} min
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        +{breach.breach_amount_minutes.toFixed(0)} min
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, color, trend }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
};

export default SLAComplianceTab;
